import { useCallback, useEffect, useState } from "react";
import api, { pickErr } from "@/lib/api";
import { notifyError, notifySuccess } from "@/lib/notify";

export default function Categories() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/categories");
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(pickErr(err, "Failed to load categories"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function onCreate(e) {
    e.preventDefault();
    setFormError("");
    setSuccess("");
    setSubmitting(true);
    try {
      await api.post("/categories", {
        name: name.trim(),
        description: description.trim() || null,
      });
      setName("");
      setDescription("");
      setSuccess("Category created.");
      notifySuccess("Category created");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to create category");
      setFormError(msg);
      notifyError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  async function onDelete(id) {
    if (!window.confirm("Delete this category?")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/categories/${id}`);
      setSuccess("Category deleted.");
      notifySuccess("Category deleted");
      await load();
    } catch (err) {
      const msg = pickErr(err, "Failed to delete category");
      setError(msg);
      notifyError(msg);
    }
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-900 mb-1">Categories</h2>
      <p className="text-sm text-slate-500 mb-6">
        Test categories used to group questions (e.g. Psychology, Career).
      </p>

      <section className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">
          Create a category
        </h3>
        <form
          onSubmit={onCreate}
          className="grid sm:grid-cols-[1fr_2fr_auto] gap-3 items-end"
          data-testid="categories-create-form"
        >
          <div>
            <label className="block text-xs text-slate-600 mb-1">Name</label>
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="categories-create-name"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">
              Description (optional)
            </label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="categories-create-description"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="categories-create-submit"
            className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
          >
            {submitting ? "Creating…" : "Create"}
          </button>
        </form>
        {formError ? (
          <p
            data-testid="categories-create-error"
            className="mt-3 text-sm text-red-600"
          >
            {formError}
          </p>
        ) : null}
      </section>

      {success ? (
        <p
          data-testid="categories-success"
          className="mb-4 text-sm text-emerald-700"
        >
          {success}
        </p>
      ) : null}

      <section className="bg-white border border-slate-200 rounded-lg">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">
            All categories ({list.length})
          </h3>
          <button
            onClick={load}
            className="text-xs text-slate-600 hover:text-slate-900"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="px-5 py-8 text-sm text-slate-500">Loading…</p>
        ) : error ? (
          <p
            data-testid="categories-load-error"
            className="px-5 py-8 text-sm text-red-600"
          >
            {error}
          </p>
        ) : list.length === 0 ? (
          <p className="px-5 py-8 text-sm text-slate-500">No categories yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="categories-table">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-5 py-2 font-medium">Name</th>
                <th className="px-5 py-2 font-medium">Description</th>
                <th className="px-5 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-5 py-2 text-slate-900">{c.name}</td>
                  <td className="px-5 py-2 text-slate-600">
                    {c.description || "—"}
                  </td>
                  <td className="px-5 py-2 text-right">
                    <button
                      onClick={() => onDelete(c.id)}
                      data-testid={`categories-delete-${c.id}`}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
